import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { clearIdleState } from './useIdleTimeout'
import { AuthContext, type AuthContextValue, type AuthUser } from './contexts'
import { nombreDelToken } from './nombre'
import { atiendeConversaciones, roleFromGroups } from './roles'
import {
  JWT_KEY,
  ID_TOKEN_KEY,
  TOKENS_REFRESHED,
  clearSession,
  exchangeCodeForTokens,
  redirectToLogin,
  refreshTokens,
  sessionAgeExceeded,
} from './session'


// ── JWT helpers ───────────────────────────────────────────────────────────────

function parseJwt(token: string): AuthUser | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = parts[1]
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
    const parsed = JSON.parse(decoded) as Record<string, unknown>

    const email = typeof parsed['email'] === 'string' ? parsed['email'] : ''
    const exp = typeof parsed['exp'] === 'number' ? parsed['exp'] : 0

    let groups: string[] = []
    const rawGroups = parsed['cognito:groups']
    if (Array.isArray(rawGroups)) {
      groups = rawGroups.filter((g): g is string => typeof g === 'string')
    } else if (typeof rawGroups === 'string') {
      groups = rawGroups.split(',').map((g) => g.trim()).filter(Boolean)
    }

    return { email, name: nombreDelToken(parsed), groups, exp }
  } catch {
    return null
  }
}

function isTokenExpired(user: AuthUser): boolean {
  if (user.exp === 0) return false
  return Date.now() / 1000 > user.exp
}

// ── Dev bypass ────────────────────────────────────────────────────────────────

function devUser(): AuthUser | null {
  const role = import.meta.env.VITE_DEV_USER as string | undefined
  if (!role) return null
  // VITE_DEV_USER=asesor | supervisor | admin. Es el equivalente al switch
  // "VER PORTAL COMO" del prototipo: sirve para revisar las tres
  // navegaciones sin tener tres cuentas en Cognito. Solo funciona en `npm run
  // dev`; en el build la variable no está definida.
  const group =
    role === 'admin' ? 'Admin' : role === 'supervisor' ? 'Supervisor' : 'Asesor'
  return { email: `dev-${role}@prestamype.com`, name: '', groups: [group], exp: 0 }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const dev = devUser()
    if (dev) return dev

    const idToken = sessionStorage.getItem(ID_TOKEN_KEY)
    const accessToken = sessionStorage.getItem(JWT_KEY)

    // Sin tokens previos no hay sesión que evaluar. Salir ACÁ es lo que permite
    // que funcione el login: en /auth/callback todavía no hay tokens y el único
    // dato en sessionStorage es el verifier PKCE. Si se llamara a clearSession()
    // en este punto se borraría ese verifier justo antes de que
    // exchangeCodeForTokens() lo necesite, y el login quedaría en bucle.
    if (!idToken || !accessToken) return null

    // Con una sesión previa sí aplica el tope duro de 12 h: aunque el access
    // token siga vigente, una sesión más vieja que eso no se restaura.
    if (sessionAgeExceeded()) {
      clearSession()
      return null
    }

    const parsed = parseJwt(idToken)
    if (parsed && !isTokenExpired(parsed)) return parsed
    // Token vencido pero sesión todavía dentro de las 12 h: el interceptor de
    // api/client.ts lo renovará en la primera llamada. No se borra nada acá.
    return null
  })

  // true only while processing the PKCE ?code= callback
  const [isLoading, setIsLoading] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.has('code')
  })

  // On mount: handle PKCE callback if ?code= is present
  useEffect(() => {
    if (devUser()) return

    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (!code) return

    window.history.replaceState({}, document.title, window.location.pathname)
    setIsLoading(true)

    exchangeCodeForTokens(code).then((idToken) => {
      if (idToken) {
        const parsed = parseJwt(idToken)
        if (parsed && !isTokenExpired(parsed)) {
          setUser(parsed)
          setIsLoading(false)
          return
        }
      }
      // Token invalid — show login screen again
      setIsLoading(false)
    })
  }, [])

  // Renovación silenciosa: si el access token venció pero la sesión sigue dentro
  // de las 12 h, se recupera sin mandar al login. También refleja en el estado
  // las renovaciones que dispara el interceptor de api/client.ts.
  useEffect(() => {
    if (devUser()) return

    const syncFromStorage = () => {
      const idToken = sessionStorage.getItem(ID_TOKEN_KEY)
      if (!idToken) return
      const parsed = parseJwt(idToken)
      if (parsed && !isTokenExpired(parsed)) setUser(parsed)
    }

    window.addEventListener(TOKENS_REFRESHED, syncFromStorage)

    const idToken = sessionStorage.getItem(ID_TOKEN_KEY)
    const parsed = idToken ? parseJwt(idToken) : null
    if (parsed && isTokenExpired(parsed) && !sessionAgeExceeded()) {
      void refreshTokens().then((ok) => { if (ok) syncFromStorage() })
    }

    return () => window.removeEventListener(TOKENS_REFRESHED, syncFromStorage)
  }, [])

  const login = useCallback(() => { void redirectToLogin() }, [])

  const logout = useCallback(() => {
    clearSession()
    clearIdleState()
    setUser(null)
  }, [])

  // El rol tiene que ser EXPLÍCITO: si el usuario logeado no está en ninguno de
  // los 3 grupos, no se le asume el más bajo. Así ve un aviso claro ("Acceso
  // pendiente") en vez de un portal lleno de 403.
  const role = useMemo(() => roleFromGroups(user?.groups ?? []), [user])

  const isAdmin      = role === 'admin'
  const isSupervisor = role === 'supervisor'
  const isAsesor     = role === 'asesor'
  const canSupervise = isAdmin || isSupervisor
  const canOperate   = isAdmin || isSupervisor || isAsesor
  // Pertenencia al grupo, no rol efectivo: quien está en Asesor recibe
  // conversaciones por round-robin aunque además sea admin.
  const atiende      = atiendeConversaciones(user?.groups ?? [])
  const hasNoRole    = user !== null && role === null

  const value = useMemo<AuthContextValue>(
    () => ({
      user, role, isAdmin, isSupervisor, isAsesor, canOperate, canSupervise, atiende, hasNoRole,
      isAuthenticated: user !== null, isLoading, login, logout,
    }),
    [user, role, isAdmin, isSupervisor, isAsesor, canOperate, canSupervise, atiende,
     hasNoRole, isLoading, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
